import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from 'sonner';
import s from './NovaApp.module.sass';
import { useAppContext } from '../../app-core/AppContext';
import NovaThemeContext, { useNovaModeState } from './NovaThemeContext';
import NovaNav from './NovaNav';
import NovaHeader from './NovaHeader';
import NovaDashboard from './pages/NovaDashboard';
import NovaWallet from './pages/NovaWallet';
import NovaTrading from './pages/NovaTrading';
import NovaBroker from './pages/NovaBroker';
import NovaMailbox from './pages/NovaMailbox';
import NovaSettings from './pages/NovaSettings';
import NovaTooltip from './NovaTooltip';

import SiteSettingsPage from '../../components/SiteSettings/SiteSettings';

// Лоадер перехода включается ТОЛЬКО при запуске через start-branded.command
// (лаунчер выставляет REACT_APP_BRANDED=1). В обычном dev/проде его нет.
const BRANDED = process.env.REACT_APP_BRANDED === '1';

// ── Скелетон-примитивы ────────────────────────────────────────
const SkHead = () => (
    <div className={s.skHead}>
        <span className={`${s.sk} ${s.skTitle}`} />
        <span className={`${s.sk} ${s.skSub}`} />
    </div>
);
const SkStats = ({ n = 4 }) => (
    <div className={s.skStats}>
        {Array.from({ length: n }).map((_, i) => (
            <div key={i} className={s.skCard}>
                <span className={`${s.sk} ${s.skLineSm}`} />
                <span className={`${s.sk} ${s.skLineLg}`} />
                <span className={`${s.sk} ${s.skLineXs}`} />
            </div>
        ))}
    </div>
);
const SkRows = ({ n = 4 }) => (
    <div className={s.skRows}>
        {Array.from({ length: n }).map((_, i) => (
            <div key={i} className={s.skRow}>
                <span className={`${s.sk} ${s.skAvatar}`} />
                <span className={`${s.sk} ${s.skLineWide}`} />
                <span className={`${s.sk} ${s.skPill}`} />
            </div>
        ))}
    </div>
);

// Раскладки скелетона под конкретные разделы Nova
const SK_LAYOUTS = {
    // Дашборд: hero-статы + график + боковые карточки
    dashboard: () => (
        <>
            <SkHead />
            <SkStats n={4} />
            <div className={s.skTwoCol}>
                <div className={`${s.sk} ${s.skChart} ${s.skMain}`} />
                <div className={s.skSide}>
                    <div className={`${s.sk} ${s.skSideCard}`} />
                    <div className={`${s.sk} ${s.skSideCard}`} />
                </div>
            </div>
        </>
    ),
    // Брокер: статы + широкий график + таблица сделок
    broker: () => (
        <>
            <SkHead />
            <SkStats n={4} />
            <div className={`${s.sk} ${s.skChart}`} />
            <SkRows n={5} />
        </>
    ),
    // Кошелёк: крупная карта баланса + список транзакций
    wallet: () => (
        <>
            <SkHead />
            <div className={`${s.sk} ${s.skBalance}`} />
            <SkRows n={6} />
        </>
    ),
    // Торговля: большой график + боковая панель ордера
    trade: () => (
        <>
            <SkHead />
            <div className={s.skTwoCol}>
                <div className={`${s.sk} ${s.skChartTall} ${s.skMain}`} />
                <div className={s.skSide}>
                    <div className={`${s.sk} ${s.skField}`} />
                    <div className={`${s.sk} ${s.skField}`} />
                    <div className={`${s.sk} ${s.skField}`} />
                    <div className={`${s.sk} ${s.skBtn}`} />
                </div>
            </div>
        </>
    ),
    // Почта: тулбар + длинный список писем
    mailbox: () => (
        <>
            <SkHead />
            <div className={`${s.sk} ${s.skToolbar}`} />
            <SkRows n={7} />
        </>
    ),
    // Настройки: таб-строка + сетка полей формы
    settings: () => (
        <>
            <SkHead />
            <div className={s.skTabs}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <span key={i} className={`${s.sk} ${s.skTab}`} />
                ))}
            </div>
            <div className={s.skFormGrid}>
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className={s.skFormField}>
                        <span className={`${s.sk} ${s.skLineXs}`} />
                        <span className={`${s.sk} ${s.skField}`} />
                    </div>
                ))}
            </div>
        </>
    ),
};
SK_LAYOUTS['site-settings'] = SK_LAYOUTS.settings;

// Скелетон-заглушка контент-области под конкретный раздел
function NovaSectionLoader({ section }) {
    const Layout = SK_LAYOUTS[section] || SK_LAYOUTS.dashboard;
    return (
        <div className={s.loader} aria-hidden="true">
            <Layout />
        </div>
    );
}

/**
 * NOVA — оболочка (skin). Своя вёрстка навигации, хедера и главных страниц.
 * Логику НЕ держит: всё берёт из общего контекста (useAppContext), который
 * наполняет App.js. Оверлеи (модалки, админка, чат, тосты) остаются общими в
 * App.js и работают за счёт тех же сеттеров, что дёргает Nova.
 *
 * Тяжёлые самодостаточные страницы (trade / mailbox / broker / settings /
 * site-settings) переиспользуются как есть — они сами считают ширину под
 * рельс 280px, поэтому геометрия рельса сохранена, а весь визуал новый.
 */
export default function NovaApp() {
    const ctx = useAppContext();
    const {
        showExactMenu,
        isNavbarCollapsed,
        siteSettings = {},
        canAccessSiteSettingsPage,
        currentConfigFile,
        handleSaveSettings,
    } = ctx;

    const renderPage = () => {
        switch (showExactMenu) {
            case 'trade':
                return <NovaTrading />;
            case 'wallet':
                return <NovaWallet />;
            case 'mailbox':
                return (
                    <NovaMailbox
                        key={`mailbox-${siteSettings.currentLanguage}-${siteSettings.translationsVersion}-${siteSettings.selectedFlag}`}
                    />
                );
            case 'broker':
                return <NovaBroker />;
            case 'settings':
                return <NovaSettings />;
            case 'site-settings':
                return canAccessSiteSettingsPage ? (
                    <SiteSettingsPage
                        settings={siteSettings}
                        onSaveSettings={handleSaveSettings}
                        isNavbarCollapsed={isNavbarCollapsed}
                        currentConfigFile={currentConfigFile}
                    />
                ) : (
                    <div className={s.restricted}>
                        <h2>Доступ ограничен</h2>
                        <p>Для доступа к настройкам сайта требуется роль администратора, тимлида или доверенного пользователя.</p>
                    </div>
                );
            case 'dashboard':
            default:
                return <NovaDashboard />;
        }
    };

    // Переиспользуемые страницы сами считают ширину под рельс — им своя обёртка,
    // собственным страницам Nova даём фирменные отступы.
    const isReusedPage = ['site-settings'].includes(showExactMenu);

    // Светлая/тёмная тема: значение переключает всю палитру токенов через атрибут
    const themeState = useNovaModeState();

    // Рандомный лоадер 1–3 c при смене раздела (только branded-локалка).
    // Состояние вычисляем СИНХРОННО во время рендера (паттерн производного
    // состояния): как только раздел сменился — тут же включаем лоадер, ещё до
    // коммита в DOM. Так на первом же кадре нового раздела уже виден скелетон,
    // без проскока «загруженного» контента (эффекты срабатывают позже).
    const prevSectionRef = useRef(null);
    const [loadingSection, setLoadingSection] = useState(BRANDED ? showExactMenu : null);
    if (BRANDED && prevSectionRef.current !== showExactMenu) {
        prevSectionRef.current = showExactMenu;
        setLoadingSection(showExactMenu); // setState в рендере → React перерендерит сразу
    }
    // Таймер прячет лоадер через 1–3 c
    useEffect(() => {
        if (!BRANDED || !loadingSection) return undefined;
        const duration = 1000 + Math.random() * 2000; // 1000–3000 мс
        const id = setTimeout(() => setLoadingSection(null), duration);
        return () => clearTimeout(id);
    }, [loadingSection]);

    return (
        <NovaThemeContext.Provider value={themeState}>
            <div
                className={`${s.nova} ${isNavbarCollapsed ? s.collapsed : ''}`}
                data-nova-theme
                data-nova-mode={themeState.mode}
            >
                {/* Sonner-тосты Nova: внутри .nova, чтобы работали CSS-токены темы */}
                <Toaster
                    theme={themeState.mode}
                    position="top-center"
                    gap={10}
                    offset={{ top: 74 }}
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: 'var(--surface-2)',
                            color: 'var(--ink)',
                            border: '1px solid var(--hairline-strong)',
                            borderRadius: 'var(--r-md)',
                            boxShadow: 'var(--card-highlight), var(--pop-shadow)',
                            fontFamily: 'inherit',
                            fontSize: '13.5px',
                        },
                    }}
                />
                <NovaTooltip />
                <NovaNav />
                <div className={s.main}>
                    <NovaHeader />
                        <main className={`${s.content} ${isReusedPage ? s.contentReused : ''} ${!isReusedPage && !['wallet', 'trade', 'broker', 'mailbox'].includes(showExactMenu) ? s.contentFill : ''}`}>
                        {renderPage()}
                    </main>
                    {loadingSection && <NovaSectionLoader section={showExactMenu} />}
                </div>
            </div>
        </NovaThemeContext.Provider>
    );
}
